import { useNavigate } from 'react-router-dom'
import { useEffect, useState, useRef } from 'react'
import {
  Calendar, Zap, Cloud, DollarSign, BarChart2, Users,
  Check, X, ArrowRight, Waves, ArrowUpRight, Menu, ChevronRight
} from 'lucide-react'

// ─── Design tokens ────────────────────────────────────────────────────────────
const C = {
  ink:    '#06090F',
  ink1:   '#0C1220',
  ink2:   '#111827',
  coral:  '#E8614A',
  coralD: '#C04030',
  teal:   '#0EAAA4',
  tealD:  '#0A8A85',
  gold:   '#D4A84B',
  sand:   '#F5EDD8',
  sandMd: 'rgba(245,237,216,0.12)',
  sandSm: 'rgba(245,237,216,0.06)',
  w100:   '#FFFFFF',
  w80:    'rgba(255,255,255,0.80)',
  w50:    'rgba(255,255,255,0.50)',
  w24:    'rgba(255,255,255,0.24)',
  w12:    'rgba(255,255,255,0.12)',
  w06:    'rgba(255,255,255,0.06)',
  w03:    'rgba(255,255,255,0.03)',
  ease:   'cubic-bezier(0.16,1,0.3,1)',
}

const SLIDES = [
  { src: '/screen-prices.png',      label: 'Schedule',           dot: C.teal  },
  { src: '/screen-schedule.png',    label: 'Pricing',            dot: C.gold  },
  { src: '/screen-instructors.png', label: 'Instructors',        dot: C.coral },
  { src: '/screen-weather.png',     label: 'Live Wave Forecast', dot: C.teal  },
]

const FEATURES = [
  {
    icon: Calendar,
    n: '01',
    title: 'Lessons & Camp Scheduling',
    desc: 'Drag-and-drop scheduling for surf lessons and camps. See your entire week at a glance.',
    accent: C.teal,
    span: '2 / span 2',
    row: 'span 1',
    bg: `linear-gradient(140deg, rgba(14,170,164,0.14) 0%, rgba(14,170,164,0.04) 100%)`,
    border: 'rgba(14,170,164,0.22)',
  },
  {
    icon: Zap,
    n: '02',
    title: 'AI Instructor Matching',
    desc: 'Auto-pair customers with the right instructor based on skill level, specialty, and real-time availability.',
    accent: C.coral,
    span: '4 / span 1',
    row: 'span 1',
    bg: `linear-gradient(140deg, rgba(232,97,74,0.14) 0%, rgba(232,97,74,0.04) 100%)`,
    border: 'rgba(232,97,74,0.22)',
  },
  {
    icon: Cloud,
    n: '03',
    title: 'Live Weather & Surf',
    desc: 'Real-time wave height, swell, wind, UV, water temp, and NOAA tides for every beach.',
    accent: C.gold,
    span: '1 / span 1',
    row: 'span 2',
    bg: `linear-gradient(140deg, rgba(212,168,75,0.14) 0%, rgba(212,168,75,0.04) 100%)`,
    border: 'rgba(212,168,75,0.22)',
  },
  {
    icon: DollarSign,
    n: '04',
    title: 'Your Own Booking Link',
    desc: 'Every school gets a custom link. Customers book and pay directly — funds hit your account and the lesson drops into your schedule automatically.',
    accent: C.teal,
    span: '2 / span 2',
    row: 'span 1',
    bg: `linear-gradient(140deg, rgba(14,170,164,0.10) 0%, rgba(14,170,164,0.03) 100%)`,
    border: 'rgba(14,170,164,0.18)',
  },
  {
    icon: BarChart2,
    n: '05',
    title: 'Instructor Lesson Requests',
    desc: 'Instructors request lessons before you assign anyone. You stay in control — they stay engaged.',
    accent: C.coral,
    span: '4 / span 1',
    row: 'span 1',
    bg: `linear-gradient(140deg, rgba(232,97,74,0.10) 0%, rgba(232,97,74,0.03) 100%)`,
    border: 'rgba(232,97,74,0.18)',
  },
  {
    icon: Users,
    n: '06',
    title: 'Payroll & Analytics',
    desc: 'Time logs flow straight into payroll. Revenue trends, instructor performance, busiest days — all in one place.',
    accent: C.gold,
    span: '2 / span 3',
    row: 'span 1',
    bg: `linear-gradient(140deg, rgba(212,168,75,0.10) 0%, rgba(212,168,75,0.03) 100%)`,
    border: 'rgba(212,168,75,0.18)',
  },
]

const STEPS = [
  { n: '01', title: 'Sign up & subscribe',   desc: 'Create your admin account and choose your plan in under 5 minutes.',         color: C.teal  },
  { n: '02', title: 'Set up your school',    desc: 'Add your school name, beach locations, and lesson types.',                   color: C.coral },
  { n: '03', title: 'Invite instructors',    desc: 'Instructors sign up, pick your school, and fill in their availability.',     color: C.gold  },
  { n: '04', title: 'Start taking bookings', desc: 'Customers book online. You schedule. AI matches. Everyone shows up ready.', color: C.teal  },
]

const PLANS = [
  {
    name: 'Starter', tag: 'Small Schools · Seasonal', mo: '50', annual: '599',
    accent: C.teal, featured: false,
    items: [
      [1, 'Scheduling & calendar'],
      [1, 'Instructor management'],
      [1, 'Customer booking link'],
      [1, 'Stripe payments'],
      [1, 'Live surf & weather'],
      [1, 'Up to 7 instructors'],
      [0, 'AI instructor matching'],
      [0, 'Payroll & CSV export'],
      [0, 'Analytics dashboard'],
    ],
  },
  {
    name: 'Standard', tag: 'Growing Schools · Full Ops', mo: '108', annual: '1,299',
    accent: C.gold, featured: false,
    items: [
      [1, 'Scheduling & calendar'],
      [1, 'Instructor management'],
      [1, 'Customer booking link'],
      [1, 'Stripe payments'],
      [1, 'Live surf & weather'],
      [1, 'Up to 14 instructors'],
      [1, 'AI instructor matching'],
      [1, 'Payroll & CSV export'],
      [0, 'Analytics dashboard'],
    ],
  },
  {
    name: 'Pro', tag: 'Full Platform · Unlimited', mo: '183', annual: '2,199',
    accent: C.coral, featured: true,
    items: [
      [1, 'Scheduling & calendar'],
      [1, 'Instructor management'],
      [1, 'Customer booking link'],
      [1, 'Stripe payments'],
      [1, 'Live surf & weather'],
      [1, 'Unlimited instructors'],
      [1, 'AI instructor matching'],
      [1, 'Payroll & CSV export'],
      [1, 'Analytics dashboard'],
    ],
  },
]

// ─── Carousel ─────────────────────────────────────────────────────────────────
function Carousel() {
  const [idx, setIdx]   = useState(0)
  const [exit, setExit] = useState(false)

  useEffect(() => {
    const t = setInterval(() => {
      setExit(true)
      setTimeout(() => { setIdx(i => (i + 1) % SLIDES.length); setExit(false) }, 300)
    }, 4200)
    return () => clearInterval(t)
  }, [])

  const jump = i => { if (i === idx) return; setExit(true); setTimeout(() => { setIdx(i); setExit(false) }, 240) }
  const s = SLIDES[idx]

  return (
    <div style={{ width: '100%', maxWidth: 980, margin: '0 auto' }}>
      {/* Browser chrome */}
      <div style={{
        borderRadius: 16,
        overflow: 'hidden',
        boxShadow: `0 2px 0 rgba(255,255,255,0.08) inset, 0 60px 140px rgba(0,0,0,0.6), 0 0 0 1px rgba(255,255,255,0.06)`,
        background: '#0D0D0D',
      }}>
        <div style={{
          background: '#181818',
          padding: '11px 16px',
          display: 'flex',
          alignItems: 'center',
          gap: 10,
          borderBottom: '1px solid rgba(255,255,255,0.05)',
        }}>
          <div style={{ display: 'flex', gap: 6 }}>
            {['#FF5F57', '#FEBC2E', '#2BC840'].map(c => (
              <div key={c} style={{ width: 11, height: 11, borderRadius: '50%', background: c }} />
            ))}
          </div>
          <div style={{
            flex: 1,
            background: 'rgba(255,255,255,0.05)',
            borderRadius: 7,
            padding: '4px 12px',
            fontSize: 11,
            color: 'rgba(255,255,255,0.32)',
            border: '1px solid rgba(255,255,255,0.06)',
            display: 'flex',
            alignItems: 'center',
            gap: 6,
            maxWidth: 200,
            margin: '0 auto',
            letterSpacing: 0.3,
            fontFamily: '"JetBrains Mono",monospace',
          }}>
            <Waves size={8} color={C.teal} style={{ flexShrink: 0 }} />
            app.wavesync.cloud
          </div>
          <div style={{ marginLeft: 'auto' }} />
        </div>
        <div style={{ position: 'relative', overflow: 'hidden', lineHeight: 0, background: '#0B1520' }}>
          <img
            key={idx}
            src={s.src}
            alt={s.label}
            style={{
              width: '100%',
              display: 'block',
              animation: exit
                ? `exitLeft .3s ${C.ease} forwards`
                : `enterRight .4s ${C.ease} both`,
              willChange: 'transform,opacity',
            }}
          />
          <div style={{
            position: 'absolute', bottom: 0, left: 0, right: 0, height: 60,
            background: 'linear-gradient(to bottom,transparent,rgba(6,9,15,0.7))',
            pointerEvents: 'none', zIndex: 2,
          }} />
        </div>
      </div>

      {/* Tab pills */}
      <div style={{ display: 'flex', justifyContent: 'center', gap: 8, marginTop: 24, flexWrap: 'wrap' }}>
        {SLIDES.map((sl, i) => (
          <button
            key={sl.label}
            onClick={() => jump(i)}
            style={{
              display: 'flex', alignItems: 'center', gap: 7,
              background: i === idx ? C.sandMd : C.w03,
              border: `1px solid ${i === idx ? 'rgba(245,237,216,0.20)' : C.w06}`,
              color: i === idx ? C.sand : C.w50,
              padding: '8px 16px', borderRadius: 999, cursor: 'pointer',
              fontFamily: 'inherit', fontSize: 12, fontWeight: i === idx ? 600 : 400,
              transition: `all .3s ${C.ease}`,
              letterSpacing: 0.2,
            }}
          >
            <span style={{
              width: 6, height: 6, borderRadius: '50%',
              background: i === idx ? sl.dot : C.w24,
              flexShrink: 0,
              boxShadow: i === idx ? `0 0 8px ${sl.dot}` : 'none',
              transition: 'all .3s',
            }} />
            {sl.label}
          </button>
        ))}
      </div>
    </div>
  )
}

// ─── Main page ────────────────────────────────────────────────────────────────
export default function LandingPage() {
  const navigate  = useNavigate()
  const [scrolled, setScrolled] = useState(false)
  const [mobileOpen, setMobileOpen] = useState(false)
  const heroRef = useRef(null)

  useEffect(() => {
    const fn = () => setScrolled(window.scrollY > 48)
    window.addEventListener('scroll', fn, { passive: true })
    return () => window.removeEventListener('scroll', fn)
  }, [])

  const scrollTo = id => {
    setMobileOpen(false)
    document.getElementById(id)?.scrollIntoView({ behavior: 'smooth', block: 'start' })
  }

  return (
    <div style={{
      minHeight: '100vh',
      background: C.ink,
      color: C.w80,
      fontFamily: '"DM Sans",-apple-system,sans-serif',
      overflowX: 'hidden',
    }}>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Playfair+Display:ital,wght@0,700;0,900;1,700;1,900&family=DM+Sans:wght@300;400;500;600;700&family=JetBrains+Mono:wght@400;500&display=swap');
        *, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; }
        html { scroll-behavior: smooth; }
        body { background: ${C.ink} !important; }

        @keyframes enterRight { from { opacity:0; transform:translateX(20px) } to { opacity:1; transform:translateX(0) } }
        @keyframes exitLeft   { from { opacity:1; transform:translateX(0) } to { opacity:0; transform:translateX(-20px) } }
        @keyframes fadeUp     { from { opacity:0; transform:translateY(28px) } to { opacity:1; transform:translateY(0) } }
        @keyframes floatA     { 0%,100%{transform:translate(0,0) scale(1)} 50%{transform:translate(40px,-30px) scale(1.06)} }
        @keyframes floatB     { 0%,100%{transform:translate(0,0) scale(1)} 45%{transform:translate(-50px,40px) scale(1.04)} }
        @keyframes floatC     { 0%,100%{transform:translate(0,0) scale(1)} 55%{transform:translate(20px,50px) scale(1.08)} }
        @keyframes pulseDot   { 0%,100%{opacity:1;transform:scale(1)} 50%{opacity:.5;transform:scale(0.75)} }
        @keyframes shimmer    { 0%{background-position:-200% center} 100%{background-position:200% center} }

        .aUp  { animation: fadeUp .6s cubic-bezier(.16,1,.3,1) both }
        .aUp1 { animation: fadeUp .6s cubic-bezier(.16,1,.3,1) .08s both }
        .aUp2 { animation: fadeUp .6s cubic-bezier(.16,1,.3,1) .18s both }
        .aUp3 { animation: fadeUp .6s cubic-bezier(.16,1,.3,1) .30s both }
        .aUp4 { animation: fadeUp .6s cubic-bezier(.16,1,.3,1) .42s both }
        .aUp5 { animation: fadeUp .6s cubic-bezier(.16,1,.3,1) .54s both }

        .btn-primary {
          cursor: pointer; border: none; font-family: inherit; font-weight: 700;
          display: inline-flex; align-items: center; gap: 8px;
          transition: transform .25s cubic-bezier(.16,1,.3,1), box-shadow .25s, opacity .18s;
        }
        .btn-primary:hover { transform: translateY(-2px) scale(1.02); opacity: .92; }
        .btn-primary:active { transform: translateY(0) scale(.98); }

        .btn-ghost {
          cursor: pointer; font-family: inherit; font-weight: 600;
          display: inline-flex; align-items: center; gap: 8px;
          transition: background .2s, border-color .2s, color .2s;
        }
        .btn-ghost:hover { background: rgba(255,255,255,0.08) !important; border-color: rgba(255,255,255,0.22) !important; }

        .bento-card {
          transition: transform .3s cubic-bezier(.16,1,.3,1), box-shadow .3s, border-color .25s !important;
        }
        .bento-card:hover {
          transform: translateY(-4px) !important;
          box-shadow: 0 20px 60px rgba(0,0,0,0.4) !important;
        }

        .step-card {
          transition: border-color .25s, transform .3s cubic-bezier(.16,1,.3,1) !important;
        }
        .step-card:hover {
          transform: translateY(-3px) !important;
        }

        .price-card {
          transition: transform .3s cubic-bezier(.16,1,.3,1), box-shadow .3s !important;
        }
        .price-card:hover { transform: translateY(-6px) !important; }

        .nav-link {
          background: none; border: none; font-family: inherit; font-weight: 500;
          cursor: pointer; padding: 0; font-size: 14px; color: ${C.w50};
          transition: color .18s;
        }
        .nav-link:hover { color: ${C.sand} !important; }

        .sand-link { color: ${C.teal}; text-decoration: none; transition: color .18s; font-weight: 600; }
        .sand-link:hover { color: ${C.sand}; }

        @media (max-width: 960px) {
          .bento-grid { grid-template-columns: 1fr 1fr !important; }
          .bento-grid > * { grid-column: span 1 !important; grid-row: span 1 !important; }
          .price-grid { grid-template-columns: 1fr !important; }
          .steps-grid { grid-template-columns: 1fr 1fr !important; }
          .hero-headline { font-size: clamp(52px,10vw,88px) !important; }
          .nav-center { display: none !important; }
        }
        @media (max-width: 600px) {
          .steps-grid { grid-template-columns: 1fr !important; }
          .cta-row { flex-direction: column !important; align-items: stretch !important; }
          .hero-headline { font-size: clamp(44px,13vw,72px) !important; }
          .stat-bar { flex-direction: column !important; gap: 24px !important; }
          .footer-inner { flex-direction: column !important; align-items: center !important; text-align: center !important; gap: 20px !important; }
        }
        @media (prefers-reduced-motion: reduce) {
          *, *::before, *::after { animation-duration: .01ms !important; transition-duration: .01ms !important; }
        }
      `}</style>

      {/* ── NAV ──────────────────────────────────────────────────────────────── */}
      <nav style={{
        position: 'fixed', top: 0, left: 0, right: 0, zIndex: 600,
        height: 64,
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        padding: '0 clamp(20px,4vw,52px)',
        background: scrolled ? 'rgba(6,9,15,0.88)' : 'transparent',
        backdropFilter: scrolled ? 'blur(24px) saturate(160%)' : 'none',
        borderBottom: scrolled ? `1px solid ${C.w06}` : '1px solid transparent',
        transition: 'background .35s, border-color .35s',
      }}>
        {/* Logo */}
        <div
          onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
          style={{
            display: 'flex', alignItems: 'center', gap: 10, cursor: 'pointer', userSelect: 'none',
          }}
        >
          <div style={{
            width: 32, height: 32, borderRadius: 9,
            background: `linear-gradient(135deg,${C.coral},${C.coralD})`,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            boxShadow: `0 4px 16px rgba(232,97,74,0.4)`,
            flexShrink: 0,
          }}>
            <Waves size={16} color="#fff" strokeWidth={2} />
          </div>
          <span style={{
            fontFamily: '"Playfair Display",serif',
            fontSize: 20, fontWeight: 900, color: C.w100, letterSpacing: 0.2,
          }}>WaveSync</span>
        </div>

        {/* Center links */}
        <div className="nav-center" style={{ display: 'flex', gap: 32, position: 'absolute', left: '50%', transform: 'translateX(-50%)' }}>
          {[['Features', 'features-section'], ['Pricing', 'pricing-section'], ['About', 'about-section']].map(([l, id]) => (
            <button key={l} className="nav-link" onClick={() => scrollTo(id)}>{l}</button>
          ))}
        </div>

        {/* Right actions */}
        <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
          <button
            className="btn-ghost"
            onClick={() => navigate('/login')}
            style={{
              background: 'transparent',
              border: `1px solid ${C.w12}`,
              color: C.w80, padding: '8px 18px', borderRadius: 10,
              fontSize: 14,
            }}
          >
            Sign In
          </button>
          <button
            className="btn-primary"
            onClick={() => navigate('/login?signup=true')}
            style={{
              background: `linear-gradient(135deg,${C.coral},${C.coralD})`,
              color: '#fff', padding: '8px 20px', borderRadius: 10,
              fontSize: 14,
              boxShadow: `0 4px 18px rgba(232,97,74,0.38)`,
            }}
          >
            Get Started <ArrowUpRight size={14} strokeWidth={2.5} />
          </button>
        </div>
      </nav>

      {/* ── HERO ─────────────────────────────────────────────────────────────── */}
      <section
        ref={heroRef}
        style={{
          minHeight: '100vh',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          textAlign: 'center',
          padding: 'clamp(120px,14vw,160px) clamp(24px,6vw,80px) clamp(80px,8vw,100px)',
          position: 'relative',
          overflow: 'hidden',
        }}
      >
        {/* Background gradient mesh */}
        <div style={{ position: 'absolute', inset: 0, zIndex: 0, pointerEvents: 'none' }}>
          <div style={{
            position: 'absolute', top: '-10%', left: '10%',
            width: 'min(720px,60vw)', height: 'min(720px,60vw)',
            borderRadius: '50%',
            background: 'radial-gradient(circle,rgba(14,170,164,0.15) 0%,transparent 65%)',
            filter: 'blur(70px)',
            animation: 'floatA 26s ease-in-out infinite',
          }} />
          <div style={{
            position: 'absolute', bottom: '5%', right: '5%',
            width: 'min(560px,45vw)', height: 'min(560px,45vw)',
            borderRadius: '50%',
            background: 'radial-gradient(circle,rgba(232,97,74,0.12) 0%,transparent 65%)',
            filter: 'blur(60px)',
            animation: 'floatB 32s ease-in-out infinite',
          }} />
          <div style={{
            position: 'absolute', top: '40%', right: '20%',
            width: 'min(380px,30vw)', height: 'min(380px,30vw)',
            borderRadius: '50%',
            background: 'radial-gradient(circle,rgba(245,237,216,0.06) 0%,transparent 70%)',
            filter: 'blur(50px)',
            animation: 'floatC 38s ease-in-out infinite',
          }} />
          {/* Dot grid */}
          <div style={{
            position: 'absolute', inset: 0,
            backgroundImage: 'radial-gradient(rgba(245,237,216,0.04) 1.5px,transparent 1.5px)',
            backgroundSize: '40px 40px',
          }} />
          {/* Grain */}
          <svg style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', opacity: 0.04 }} xmlns="http://www.w3.org/2000/svg">
            <filter id="g"><feTurbulence type="fractalNoise" baseFrequency=".65" numOctaves="3" stitchTiles="stitch"/><feColorMatrix type="saturate" values="0"/></filter>
            <rect width="100%" height="100%" filter="url(#g)" />
          </svg>
        </div>

        <div style={{ position: 'relative', zIndex: 1, maxWidth: 900, width: '100%' }}>
          {/* Eyebrow pill */}
          <div className="aUp" style={{
            display: 'inline-flex', alignItems: 'center', gap: 8,
            background: 'rgba(14,170,164,0.08)',
            border: '1px solid rgba(14,170,164,0.24)',
            color: C.teal,
            fontFamily: '"JetBrains Mono",monospace',
            fontSize: 10, fontWeight: 500, letterSpacing: 2.5,
            padding: '7px 18px', borderRadius: 999,
            textTransform: 'uppercase', marginBottom: 40,
          }}>
            <span style={{
              width: 6, height: 6, borderRadius: '50%',
              background: C.teal, display: 'inline-block',
              boxShadow: `0 0 10px ${C.teal}`,
              animation: 'pulseDot 2s ease-in-out infinite',
            }} />
            Surf School Management Platform
          </div>

          {/* Headline */}
          <h1
            className="aUp1 hero-headline"
            style={{
              fontFamily: '"Playfair Display",serif',
              fontSize: 'clamp(56px,9vw,112px)',
              fontWeight: 900,
              lineHeight: 0.96,
              letterSpacing: '-0.038em',
              color: C.w100,
              margin: '0 0 0',
            }}
          >
            The operating
            <br />
            system{' '}
            <em style={{
              fontStyle: 'italic',
              background: `linear-gradient(120deg,${C.sand} 0%,${C.coral} 55%,#E84030 100%)`,
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              backgroundClip: 'text',
            }}>
              for surf
            </em>
            <br />
            <em style={{
              fontStyle: 'italic',
              background: `linear-gradient(120deg,${C.coral} 0%,${C.sand} 80%)`,
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              backgroundClip: 'text',
            }}>
              schools.
            </em>
          </h1>

          {/* Divider accent */}
          <div className="aUp2" style={{
            display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 16,
            margin: '40px auto 36px', maxWidth: 380,
          }}>
            <div style={{ flex: 1, height: 1, background: `linear-gradient(to right,transparent,${C.w12})` }} />
            <div style={{ display: 'flex', gap: 6 }}>
              {[C.teal, C.coral, C.gold].map(c => (
                <div key={c} style={{ width: 6, height: 6, borderRadius: '50%', background: c, boxShadow: `0 0 8px ${c}` }} />
              ))}
            </div>
            <div style={{ flex: 1, height: 1, background: `linear-gradient(to left,transparent,${C.w12})` }} />
          </div>

          {/* Subtitle */}
          <p className="aUp3" style={{
            fontSize: 'clamp(16px,1.8vw,19px)',
            color: C.w50,
            maxWidth: 500,
            margin: '0 auto 52px',
            lineHeight: 1.85,
            fontWeight: 400,
          }}>
            Scheduling, AI instructor matching, live weather, customer bookings, and payroll — built for surf schools and surf camps.
          </p>

          {/* CTAs */}
          <div className="aUp4 cta-row" style={{ display: 'flex', gap: 12, justifyContent: 'center', flexWrap: 'wrap', marginBottom: 32 }}>
            <button
              className="btn-primary"
              onClick={() => navigate('/login?signup=true')}
              style={{
                background: `linear-gradient(135deg,${C.coral},${C.coralD})`,
                color: '#fff',
                padding: 'clamp(14px,1.5vw,17px) clamp(36px,3vw,56px)',
                borderRadius: 12, fontSize: 'clamp(15px,1.4vw,17px)',
                boxShadow: `0 8px 36px rgba(232,97,74,0.44)`,
              }}
            >
              Get Started Free <ArrowRight size={17} strokeWidth={2.5} />
            </button>
            <button
              className="btn-ghost"
              onClick={() => navigate('/login')}
              style={{
                background: C.sandMd,
                border: `1px solid rgba(245,237,216,0.14)`,
                color: C.sand,
                padding: 'clamp(14px,1.5vw,17px) clamp(36px,3vw,56px)',
                borderRadius: 12, fontSize: 'clamp(15px,1.4vw,17px)',
              }}
            >
              Sign In
            </button>
          </div>

          {/* Trust lines */}
          <div className="aUp5" style={{ display: 'flex', flexDirection: 'column', gap: 8, alignItems: 'center' }}>
            <p style={{ fontSize: 12, color: C.w24, letterSpacing: 0.5, fontFamily: '"JetBrains Mono",monospace' }}>
              Cancel anytime · No contracts · Secured by Stripe
            </p>
            <p style={{ fontSize: 12, color: C.w24, fontStyle: 'italic' }}>
              Trusted by Stacey's Surf Camp in Margate NJ
            </p>
          </div>
        </div>

        {/* Scroll cue */}
        <div style={{
          position: 'absolute', bottom: 36, left: '50%', transform: 'translateX(-50%)',
          display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 6,
          zIndex: 2, opacity: scrolled ? 0 : 0.4, transition: 'opacity .4s',
        }}>
          <div style={{ width: 1, height: 40, background: `linear-gradient(to bottom,transparent,${C.sand})` }} />
          <div style={{ width: 5, height: 5, borderRadius: '50%', background: C.sand }} />
        </div>

        {/* Section wave */}
        <div style={{ position: 'absolute', bottom: -1, left: 0, right: 0, height: 80, zIndex: 2, pointerEvents: 'none' }}>
          <svg viewBox="0 0 1440 80" preserveAspectRatio="none" style={{ width: '100%', height: '100%', display: 'block' }}>
            <path d="M0,40 C480,80 960,0 1440,40 L1440,80 L0,80Z" fill={C.ink1} />
          </svg>
        </div>
      </section>

      {/* ── PRODUCT SHOWCASE ─────────────────────────────────────────────────── */}
      <section style={{ background: C.ink1, padding: '0 clamp(24px,5vw,60px) clamp(80px,9vw,110px)' }}>
        <div style={{ maxWidth: 1040, margin: '0 auto' }}>
          {/* Label */}
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 14, marginBottom: 56 }}>
            <div style={{ flex: 1, height: 1, background: `linear-gradient(to right,transparent,${C.w06})` }} />
            <span style={{
              fontFamily: '"JetBrains Mono",monospace',
              fontSize: 10, fontWeight: 500, letterSpacing: 2.6,
              color: C.w24, textTransform: 'uppercase', whiteSpace: 'nowrap',
            }}>See it in action</span>
            <div style={{ flex: 1, height: 1, background: `linear-gradient(to left,transparent,${C.w06})` }} />
          </div>
          <Carousel />
        </div>
      </section>

      {/* ── FEATURES BENTO ───────────────────────────────────────────────────── */}
      <section id="features-section" style={{ background: C.ink1, padding: 'clamp(80px,10vw,120px) clamp(24px,5vw,60px)' }}>
        <div style={{ maxWidth: 1080, margin: '0 auto' }}>
          {/* Header */}
          <div style={{ marginBottom: 64 }}>
            <div style={{
              fontFamily: '"JetBrains Mono",monospace',
              fontSize: 10, fontWeight: 500, letterSpacing: 2.6,
              color: C.teal, textTransform: 'uppercase', marginBottom: 16,
            }}>
              Built for every part of your business
            </div>
            <div style={{ display: 'flex', alignItems: 'flex-end', justifyContent: 'space-between', flexWrap: 'wrap', gap: 24 }}>
              <h2 style={{
                fontFamily: '"Playfair Display",serif',
                fontSize: 'clamp(36px,4.5vw,62px)',
                fontWeight: 900, lineHeight: 1.06,
                letterSpacing: '-0.032em', color: C.w100,
              }}>
                Everything your<br />
                <em style={{
                  fontStyle: 'italic',
                  background: `linear-gradient(120deg,${C.sand},${C.gold})`,
                  WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent', backgroundClip: 'text',
                }}>school needs.</em>
              </h2>
              <p style={{ fontSize: 15, color: C.w50, lineHeight: 1.75, maxWidth: 280 }}>
                One platform. Every tool. No juggling between apps.
              </p>
            </div>
          </div>

          {/* Bento grid */}
          <div
            className="bento-grid"
            style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(4,1fr)',
              gridAutoRows: 'minmax(200px,auto)',
              gap: 16,
            }}
          >
            {FEATURES.map((f, i) => {
              const Icon = f.icon
              return (
                <div
                  key={i}
                  className="bento-card"
                  style={{
                    gridColumn: f.span,
                    gridRow: f.row,
                    background: f.bg,
                    border: `1px solid ${f.border}`,
                    borderRadius: 20,
                    padding: '28px 26px',
                    position: 'relative',
                    overflow: 'hidden',
                    cursor: 'default',
                  }}
                >
                  {/* Top-edge glow */}
                  <div style={{
                    position: 'absolute', top: 0, left: 0, right: 0, height: 1,
                    background: `linear-gradient(to right,transparent,${f.accent}55,transparent)`,
                    pointerEvents: 'none',
                  }} />
                  {/* Corner orb */}
                  <div style={{
                    position: 'absolute', top: -60, right: -60,
                    width: 180, height: 180, borderRadius: '50%',
                    background: `radial-gradient(circle,${f.accent}18 0%,transparent 70%)`,
                    pointerEvents: 'none',
                  }} />
                  {/* Watermark */}
                  <span style={{
                    position: 'absolute', bottom: 14, right: 20,
                    fontFamily: '"Playfair Display",serif',
                    fontWeight: 900, fontSize: 64,
                    color: 'rgba(255,255,255,0.04)', lineHeight: 1,
                    letterSpacing: -4, userSelect: 'none', pointerEvents: 'none',
                  }}>{f.n}</span>

                  <div style={{
                    width: 44, height: 44, borderRadius: 13,
                    background: `${f.accent}16`,
                    border: `1px solid ${f.accent}28`,
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    marginBottom: 18,
                  }}>
                    <Icon size={20} color={f.accent} strokeWidth={1.75} />
                  </div>
                  <div style={{ fontWeight: 700, fontSize: 16, color: C.w100, marginBottom: 9, letterSpacing: '-0.02em' }}>
                    {f.title}
                  </div>
                  <div style={{ fontSize: 13.5, color: C.w50, lineHeight: 1.76 }}>
                    {f.desc}
                  </div>
                </div>
              )
            })}
          </div>
        </div>
      </section>

      {/* ── HOW IT WORKS ─────────────────────────────────────────────────────── */}
      <section style={{ background: C.ink2, borderTop: `1px solid ${C.w06}`, padding: 'clamp(80px,10vw,120px) clamp(24px,5vw,60px)' }}>
        <div style={{ maxWidth: 1080, margin: '0 auto' }}>
          {/* Header */}
          <div style={{ display: 'flex', alignItems: 'flex-end', justifyContent: 'space-between', flexWrap: 'wrap', gap: 24, marginBottom: 64 }}>
            <div>
              <div style={{
                fontFamily: '"JetBrains Mono",monospace',
                fontSize: 10, fontWeight: 500, letterSpacing: 2.6,
                color: C.coral, textTransform: 'uppercase', marginBottom: 16,
              }}>
                Get started in minutes
              </div>
              <h2 style={{
                fontFamily: '"Playfair Display",serif',
                fontSize: 'clamp(36px,4.5vw,62px)',
                fontWeight: 900, lineHeight: 1.06,
                letterSpacing: '-0.032em', color: C.w100,
              }}>
                How it works.
              </h2>
            </div>
            <button
              className="btn-primary"
              onClick={() => navigate('/login?signup=true')}
              style={{
                background: 'transparent',
                border: `1px solid ${C.w12}`,
                color: C.w80, padding: '12px 24px', borderRadius: 10,
                fontSize: 14, fontWeight: 600,
                transition: 'all .25s',
              }}
            >
              Start for free <ChevronRight size={15} strokeWidth={2} />
            </button>
          </div>

          {/* Steps */}
          <div className="steps-grid" style={{ display: 'grid', gridTemplateColumns: 'repeat(4,1fr)', gap: 16 }}>
            {STEPS.map((s, i) => (
              <div
                key={i}
                className="step-card"
                style={{
                  background: C.w03,
                  border: `1px solid ${C.w06}`,
                  borderRadius: 20,
                  padding: '28px 22px',
                  position: 'relative',
                  overflow: 'hidden',
                }}
              >
                {/* Colored top bar */}
                <div style={{
                  position: 'absolute', top: 0, left: 0, right: 0, height: 2.5,
                  borderRadius: '20px 20px 0 0',
                  background: `linear-gradient(to right,${s.color},transparent)`,
                }} />
                {/* Step number */}
                <div style={{
                  width: 48, height: 48, borderRadius: '50%',
                  background: `${s.color}12`,
                  border: `1.5px solid ${s.color}44`,
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  fontFamily: '"JetBrains Mono",monospace',
                  fontWeight: 500, fontSize: 12.5, color: s.color,
                  marginBottom: 20,
                  boxShadow: `0 0 20px ${s.color}18`,
                }}>
                  {s.n}
                </div>
                <div style={{ fontWeight: 700, fontSize: 15, color: C.w100, marginBottom: 9, letterSpacing: '-0.02em' }}>
                  {s.title}
                </div>
                <div style={{ fontSize: 13, color: C.w50, lineHeight: 1.72 }}>
                  {s.desc}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── PRICING ──────────────────────────────────────────────────────────── */}
      <section id="pricing-section" style={{ background: C.ink, padding: 'clamp(80px,10vw,120px) clamp(24px,5vw,60px)', position: 'relative', overflow: 'hidden' }}>
        {/* Large background accents */}
        <div style={{
          position: 'absolute', top: '-20%', right: '-10%',
          width: 'min(700px,55vw)', height: 'min(700px,55vw)', borderRadius: '50%',
          background: 'radial-gradient(circle,rgba(232,97,74,0.06) 0%,transparent 65%)',
          filter: 'blur(80px)', pointerEvents: 'none', zIndex: 0,
        }} />
        <div style={{
          position: 'absolute', bottom: '-10%', left: '-5%',
          width: 'min(500px,40vw)', height: 'min(500px,40vw)', borderRadius: '50%',
          background: 'radial-gradient(circle,rgba(14,170,164,0.06) 0%,transparent 65%)',
          filter: 'blur(60px)', pointerEvents: 'none', zIndex: 0,
        }} />

        <div style={{ maxWidth: 1080, margin: '0 auto', position: 'relative', zIndex: 1 }}>
          {/* Header */}
          <div style={{ textAlign: 'center', marginBottom: 72 }}>
            <div style={{
              fontFamily: '"JetBrains Mono",monospace',
              fontSize: 10, fontWeight: 500, letterSpacing: 2.6,
              color: C.gold, textTransform: 'uppercase', marginBottom: 18,
            }}>
              Simple pricing
            </div>
            <h2 style={{
              fontFamily: '"Playfair Display",serif',
              fontSize: 'clamp(36px,4.5vw,62px)',
              fontWeight: 900, lineHeight: 1.06,
              letterSpacing: '-0.032em', color: C.w100, marginBottom: 16,
            }}>
              Pick the plan
              <em style={{
                fontStyle: 'italic',
                background: `linear-gradient(120deg,${C.sand},${C.gold})`,
                WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent', backgroundClip: 'text',
              }}> that fits.</em>
            </h2>
            <p style={{ color: C.w50, fontSize: 16 }}>One annual payment. No contracts.</p>
          </div>

          {/* Cards */}
          <div className="price-grid" style={{ display: 'grid', gridTemplateColumns: 'repeat(3,1fr)', gap: 20, alignItems: 'start' }}>
            {PLANS.map((plan, i) => (
              <div
                key={plan.name}
                className="price-card"
                style={{
                  borderRadius: 22, padding: '36px 28px',
                  position: 'relative', overflow: 'hidden',
                  marginTop: plan.featured ? -8 : 0,
                  background: plan.featured
                    ? 'linear-gradient(160deg,#1A0E0A 0%,#100808 100%)'
                    : C.w03,
                  border: plan.featured
                    ? `1.5px solid rgba(232,97,74,0.38)`
                    : `1px solid ${C.w06}`,
                  boxShadow: plan.featured
                    ? `0 0 0 1px rgba(232,97,74,0.08), 0 20px 80px rgba(232,97,74,0.12), inset 0 1px 0 rgba(232,97,74,0.12)`
                    : 'none',
                }}
              >
                {/* Featured top bar */}
                {plan.featured && (
                  <div style={{
                    position: 'absolute', top: 0, left: 0, right: 0, height: 2.5,
                    background: `linear-gradient(to right,transparent,${C.coral},transparent)`,
                  }} />
                )}
                {/* Featured corner glow */}
                {plan.featured && (
                  <div style={{
                    position: 'absolute', top: -70, right: -70,
                    width: 240, height: 240, borderRadius: '50%',
                    background: 'radial-gradient(circle,rgba(232,97,74,0.14) 0%,transparent 70%)',
                    pointerEvents: 'none',
                  }} />
                )}
                {/* Best value badge */}
                {plan.featured && (
                  <div style={{ position: 'absolute', top: 18, right: 18 }}>
                    <span style={{
                      background: `linear-gradient(135deg,${C.coral},${C.coralD})`,
                      color: '#fff',
                      fontSize: 9.5, fontWeight: 700,
                      fontFamily: '"JetBrains Mono",monospace',
                      padding: '3px 11px', borderRadius: 999,
                      letterSpacing: 0.8, textTransform: 'uppercase',
                      boxShadow: `0 2px 14px rgba(232,97,74,0.4)`,
                    }}>Best Value</span>
                  </div>
                )}

                {/* Plan name & tag */}
                <div style={{ marginBottom: 24 }}>
                  <div style={{
                    fontSize: 18, fontWeight: 700,
                    color: plan.featured ? C.sand : C.w100,
                    marginBottom: 4,
                  }}>{plan.name}</div>
                  <div style={{ fontSize: 11, color: C.w24, letterSpacing: 0.2 }}>{plan.tag}</div>
                </div>

                {/* Price */}
                <div style={{ display: 'flex', alignItems: 'flex-end', gap: 3, marginBottom: 4 }}>
                  <span style={{ fontSize: 15, fontWeight: 700, color: C.w50, marginBottom: 10 }}>$</span>
                  <span style={{
                    fontFamily: '"Playfair Display",serif',
                    fontSize: 60, fontWeight: 900, lineHeight: 1,
                    letterSpacing: '-0.04em',
                    color: plan.featured ? C.sand : C.w100,
                  }}>{plan.mo}</span>
                  <span style={{ fontSize: 14, color: C.w50, marginBottom: 10, marginLeft: 2 }}>/mo</span>
                </div>
                <div style={{ fontSize: 12.5, color: C.w24, marginBottom: 28 }}>
                  1 time payment of{' '}
                  <strong style={{ color: plan.featured ? C.sand : C.w80 }}>${plan.annual}</strong>
                  {' '}/ year
                </div>

                {/* CTA */}
                <button
                  className="btn-primary"
                  onClick={() => navigate('/login?signup=true')}
                  style={{
                    width: '100%',
                    justifyContent: 'center',
                    padding: '13px 0', borderRadius: 11, fontSize: 14,
                    background: plan.featured
                      ? `linear-gradient(135deg,${C.coral},${C.coralD})`
                      : C.ink2,
                    border: plan.featured ? 'none' : `1px solid ${C.w12}`,
                    color: plan.featured ? '#fff' : C.w80,
                    boxShadow: plan.featured ? `0 4px 24px rgba(232,97,74,0.38)` : 'none',
                  }}
                >
                  Start your free trial →
                </button>

                {/* Feature list */}
                <div style={{ marginTop: 26, display: 'flex', flexDirection: 'column', gap: 8 }}>
                  {plan.items.map(([on, label]) => (
                    <div
                      key={label}
                      style={{
                        display: 'flex', alignItems: 'center', gap: 10,
                        fontSize: 13,
                        color: on
                          ? (plan.featured ? C.sand : C.w80)
                          : C.w24,
                      }}
                    >
                      {on ? (
                        <span style={{
                          width: 18, height: 18, borderRadius: '50%', flexShrink: 0,
                          background: `rgba(14,170,164,0.14)`,
                          border: '1px solid rgba(14,170,164,0.28)',
                          display: 'flex', alignItems: 'center', justifyContent: 'center',
                        }}>
                          <Check size={9} color={C.teal} strokeWidth={2.5} />
                        </span>
                      ) : (
                        <span style={{
                          width: 18, height: 18, borderRadius: '50%', flexShrink: 0,
                          background: C.w03,
                          border: `1px solid ${C.w06}`,
                          display: 'flex', alignItems: 'center', justifyContent: 'center',
                        }}>
                          <X size={8} color={C.w24} strokeWidth={2} />
                        </span>
                      )}
                      {label}
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>

          {/* Footer note */}
          <div style={{ textAlign: 'center', marginTop: 44 }}>
            <p style={{
              fontSize: 11.5, color: C.w24,
              fontFamily: '"JetBrains Mono",monospace',
              letterSpacing: 0.4, marginBottom: 14,
            }}>
              No contracts · Secured by Stripe
            </p>
            <p style={{ fontSize: 14, color: C.w50 }}>
              Need a custom plan?{' '}
              <a href="mailto:drew@wavesync.cloud" className="sand-link">Talk to us</a>
              {' '}— drew@wavesync.cloud
            </p>
          </div>
        </div>
      </section>

      {/* ── ABOUT ────────────────────────────────────────────────────────────── */}
      <section
        id="about-section"
        style={{
          background: C.ink1,
          borderTop: `1px solid ${C.w06}`,
          padding: 'clamp(80px,10vw,120px) clamp(24px,5vw,60px)',
          position: 'relative', overflow: 'hidden',
        }}
      >
        {/* Background text */}
        <div style={{
          position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center',
          zIndex: 0, pointerEvents: 'none', overflow: 'hidden',
        }}>
          <span style={{
            fontFamily: '"Playfair Display",serif',
            fontSize: 'min(24vw,280px)', fontWeight: 900, fontStyle: 'italic',
            color: 'rgba(245,237,216,0.025)', letterSpacing: '-0.04em',
            userSelect: 'none', whiteSpace: 'nowrap',
          }}>WaveSync</span>
        </div>

        <div style={{ maxWidth: 760, margin: '0 auto', textAlign: 'center', position: 'relative', zIndex: 1 }}>
          <div style={{
            fontFamily: '"JetBrains Mono",monospace',
            fontSize: 10, fontWeight: 500, letterSpacing: 2.6,
            color: C.coral, textTransform: 'uppercase', marginBottom: 20,
          }}>About</div>

          <h2 style={{
            fontFamily: '"Playfair Display",serif',
            fontSize: 'clamp(34px,4.5vw,60px)',
            fontWeight: 900, lineHeight: 1.08,
            letterSpacing: '-0.032em', color: C.w100, marginBottom: 40,
          }}>
            Built by a surf instructor,
            <br />
            <em style={{
              fontStyle: 'italic',
              background: `linear-gradient(120deg,${C.coral},${C.sand})`,
              WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent', backgroundClip: 'text',
            }}>for surf schools.</em>
          </h2>

          {/* Quote card */}
          <div style={{
            background: C.sandMd,
            border: `1px solid rgba(245,237,216,0.1)`,
            borderRadius: 20,
            padding: 'clamp(28px,3vw,44px)',
            position: 'relative',
          }}>
            <div style={{
              position: 'absolute', top: -16, left: 'clamp(28px,4vw,44px)',
              fontFamily: '"Playfair Display",serif',
              fontSize: 64, fontWeight: 900, color: C.coral,
              lineHeight: 1, opacity: 0.7,
            }}>"</div>
            <p style={{
              fontSize: 'clamp(17px,2vw,21px)',
              color: 'rgba(245,237,216,0.72)',
              lineHeight: 1.85, fontWeight: 400, fontStyle: 'italic',
              position: 'relative', zIndex: 1,
            }}>
              I spent six years teaching surf lessons on the New Jersey Shore. That experience showed me exactly what surf schools need to run smoothly — so I built WaveSync myself, from the ground up, to be that tool.
            </p>
            <div style={{ marginTop: 24, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 12 }}>
              <div style={{ width: 36, height: 1, background: C.coral, opacity: 0.5 }} />
              <span style={{ fontSize: 13, fontWeight: 600, color: C.sand, letterSpacing: 0.4 }}>Drew Mirarchi</span>
              <div style={{ width: 36, height: 1, background: C.coral, opacity: 0.5 }} />
            </div>
          </div>
        </div>
      </section>

      {/* ── FINAL CTA BAND ───────────────────────────────────────────────────── */}
      <section style={{
        background: `linear-gradient(135deg,${C.coral} 0%,#C03020 100%)`,
        padding: 'clamp(56px,7vw,80px) clamp(24px,5vw,60px)',
        position: 'relative', overflow: 'hidden',
      }}>
        <div style={{
          position: 'absolute', top: '-50%', right: '-10%',
          width: 600, height: 600, borderRadius: '50%',
          background: 'radial-gradient(circle,rgba(255,255,255,0.08) 0%,transparent 65%)',
          pointerEvents: 'none',
        }} />
        <div style={{
          maxWidth: 760, margin: '0 auto', textAlign: 'center',
          position: 'relative', zIndex: 1,
        }}>
          <h2 style={{
            fontFamily: '"Playfair Display",serif',
            fontSize: 'clamp(32px,4vw,56px)',
            fontWeight: 900, color: '#fff',
            letterSpacing: '-0.03em', lineHeight: 1.08, marginBottom: 20,
          }}>
            Ready to run your school<br />
            <em style={{ fontStyle: 'italic', opacity: 0.85 }}>the right way?</em>
          </h2>
          <p style={{ fontSize: 16, color: 'rgba(255,255,255,0.7)', marginBottom: 36, lineHeight: 1.7 }}>
            Join surf schools already using WaveSync to manage lessons, instructors, and bookings.
          </p>
          <div style={{ display: 'flex', gap: 12, justifyContent: 'center', flexWrap: 'wrap' }}>
            <button
              className="btn-primary"
              onClick={() => navigate('/login?signup=true')}
              style={{
                background: '#fff', color: C.coral,
                padding: '15px 40px', borderRadius: 12, fontSize: 16,
                boxShadow: '0 8px 32px rgba(0,0,0,0.2)',
              }}
            >
              Get Started Free <ArrowRight size={17} strokeWidth={2.5} />
            </button>
            <button
              className="btn-ghost"
              onClick={() => navigate('/login')}
              style={{
                background: 'rgba(255,255,255,0.12)',
                border: '1px solid rgba(255,255,255,0.24)',
                color: '#fff', padding: '15px 40px', borderRadius: 12, fontSize: 16,
              }}
            >
              Sign In
            </button>
          </div>
          <p style={{ fontSize: 12, color: 'rgba(255,255,255,0.4)', marginTop: 20, fontFamily: '"JetBrains Mono",monospace', letterSpacing: 0.4 }}>
            Cancel anytime · No contracts · Secured by Stripe
          </p>
        </div>
      </section>

      {/* ── FOOTER ───────────────────────────────────────────────────────────── */}
      <footer style={{
        background: C.ink,
        borderTop: `1px solid ${C.w06}`,
        padding: 'clamp(28px,3.5vw,40px) clamp(24px,4vw,52px)',
      }}>
        <div
          className="footer-inner"
          style={{
            maxWidth: 1080, margin: '0 auto',
            display: 'flex', justifyContent: 'space-between', alignItems: 'center',
            flexWrap: 'wrap', gap: 20,
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <div style={{
              width: 28, height: 28, borderRadius: 8,
              background: `linear-gradient(135deg,${C.coral},${C.coralD})`,
              display: 'flex', alignItems: 'center', justifyContent: 'center',
            }}>
              <Waves size={14} color="#fff" strokeWidth={2} />
            </div>
            <span style={{
              fontFamily: '"Playfair Display",serif',
              fontSize: 18, fontWeight: 900, color: C.w80, letterSpacing: 0.2,
            }}>WaveSync LLC</span>
          </div>

          <p style={{
            fontSize: 11, color: C.w24,
            fontFamily: '"JetBrains Mono",monospace', letterSpacing: 0.3,
          }}>
            © {new Date().getFullYear()} WaveSync · Built for surf schools.
          </p>

          <div style={{ display: 'flex', gap: 24 }}>
            {[['Sign In', () => navigate('/login')], ['Terms', () => navigate('/terms')], ['Privacy', () => navigate('/privacy')]].map(([l, a]) => (
              <button
                key={l}
                onClick={a}
                className="nav-link"
                style={{ fontSize: 13 }}
              >
                {l}
              </button>
            ))}
          </div>
        </div>
      </footer>
    </div>
  )
}
